/**
 * POSTs a public-form submission to the BiorAdmin backend.
 *
 * When `files` is empty, sends `application/json` {type, data}.
 * When `files` has at least one entry, sends `multipart/form-data` with the
 * whole {type, data} as a single `payload` text field plus each file attached
 * under the field name `attachments`.
 *
 * Abuse protection (see the backend's app/helper/abuseGuard.js):
 *  - `honeypot` is the value of the hidden trap field; it is sent under
 *    HONEYPOT_FIELD only when something was typed into it (a person never does).
 *  - `turnstileToken` is the Cloudflare Turnstile token, sent as `turnstileToken`
 *    when the widget produced one.
 * Both ride beside {type, data} (inside `payload` for multipart).
 *
 * Returns { ok, id?, error? } where error is one of:
 *   "rate-limited" | "upload-failed" | "network" | "aborted" | "server"
 *
 * The backend host comes from REACT_APP_VISITOR_API_BASE_URL (see
 * src/config/apiBase.js). With no host configured the call fails as "server"
 * without touching the network.
 */
import { getApiBase } from "../config/apiBase";

// Must match HONEYPOT_FIELD in the backend (app/helper/abuseGuard.js).
export const HONEYPOT_FIELD = "hp_contact_url";

function classifyHttpError(status) {
  if (status === 429) return "rate-limited";
  if (status === 502) return "upload-failed";
  return "server";
}

export async function submitIntegrateForm({ type, data, files, signal, honeypot, turnstileToken } = {}) {
  const baseUrl = getApiBase();
  if (!baseUrl) return { ok: false, error: "server" };
  const url = `${baseUrl}/api/integrate`;
  const fileList = Array.isArray(files) ? files.filter(Boolean) : [];

  const envelope = { type, data };
  if (typeof honeypot === "string" && honeypot.trim() !== "") envelope[HONEYPOT_FIELD] = honeypot;
  if (typeof turnstileToken === "string" && turnstileToken !== "") envelope.turnstileToken = turnstileToken;

  const init = { method: "POST", signal };
  if (fileList.length === 0) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(envelope);
  } else {
    const form = new FormData();
    form.append("payload", JSON.stringify(envelope));
    for (const f of fileList) form.append("attachments", f, f.name);
    init.body = form;
    // Do NOT set Content-Type — the browser sets the multipart boundary.
  }

  let response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    if (err?.name === "AbortError") return { ok: false, error: "aborted" };
    return { ok: false, error: "network" };
  }

  if (!response.ok) {
    return { ok: false, error: classifyHttpError(response.status) };
  }

  try {
    const body = await response.json();
    if (body && body.ok) return { ok: true, id: body.id };
    return { ok: false, error: "server" };
  } catch (_) {
    return { ok: false, error: "server" };
  }
}
